
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

import './ApyTermsTable.css';
import { useTermRatesContext } from '../../providers/TermRates/TermRates';
import { cleanInternalLinkUrl, retrieveDBOsgiUrl } from '../../utils/cob-utils';
import { IDigitalBankTerms } from '../../types/shared/DigitalBankTerms';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { Section, Row, Col, TextLine } from '../../importCL';
import { useWidthContext } from '../../providers/Width';

interface ITermsTableTerm {
    product?: string;
    term?: string;
    apy?: string;
    buttonText?: string;
    url?: string;
    date?: string;
    active?: boolean;
    loyaltyRate?: string;
    loyaltyReward?: string;
    group?: string;
}

interface IApyTermsTableProps extends MappedComponentProperties {
    metaDataOverride?: string;
    apyTermsTitle?: string;
    apyTermsSubtitle?: string;
    showLoyaltyRates?: boolean;
}

export const ApyTermsTableEditConfig = {
    emptyLabel: 'APY Terms Table',
    isEmpty: (props: PropsWithChildren<IApyTermsTableProps>) => {
        return !props || !props.apyTermsTitle;
    },
};

const ApyTermsTable = (props: PropsWithChildren<IApyTermsTableProps>): JSX.Element => {
    const { termRates } = useTermRatesContext();
    const [terms, setTerms] = useState<ITermsTableTerm[]>([]);
    const { width } = useWidthContext();

    const getDigitalBankTermType = (): keyof IDigitalBankTerms|null => {
        const pathname = window.location.href;
        if (pathname.includes('deposits/cd')) {
            return 'jumboCd';
        } else if (pathname.includes('deposits/bump-cd')) {
            return 'bumpUpCd';
        }

        return null;
    };

    const showLoyaltyRates = () => {
        const metaDataHasLoyalty: boolean = terms.some(term => !!term.loyaltyReward);
        return props.showLoyaltyRates && metaDataHasLoyalty;
    };

    const showDagger = (group: string) => {
        return showLoyaltyRates() && group === 'PromoCD' ? <sup>&dagger;&dagger;</sup>: '';
    };


    useEffect(() => {
        let termsMetadata : IDigitalBankTerms;

        if (!props.metaDataOverride && Object.keys(termRates).length !== 0) {
            termsMetadata = termRates;
        } else if (props.metaDataOverride) {
            termsMetadata = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string);
        } else {
            return;
        }

        const termsKey = getDigitalBankTermType();

        if (!termsKey) {
            return;
        }

        const terms = (termsMetadata[termsKey] as ITermsTableTerm[])
            .sort((a, b) => parseInt(a?.term ?? '') - parseInt(b?.term ?? ''))
            .map((product) => ({
                term: parseInt(product?.term ?? '').toString(),
                loyaltyRate: product.loyaltyRate,
                loyaltyReward: product.loyaltyReward,
                group: product.group,
                apy: (product?.apy ?? 0).toString(),
                buttonText: 'Open An Account',
                url: retrieveDBOsgiUrl(product.url),
                active: true,
            }));

        setTerms(terms);
    }, [termRates]);

    return (
        <Section>
            <div id='apy-terms-table' className='ApyTermsTable'>
            <div className='ApyTermsTable__container'>
                    { props.apyTermsTitle &&
                <TextLine 
                    variant='author'
                    tag='h2'
                    subHeading={props.apyTermsSubtitle ? transformHTMLParserLinks(props.apyTermsSubtitle) : ''}
                    className='mb-6'
                >
                    {transformHTMLParserLinks(props.apyTermsTitle)}
                </TextLine>
            }
            </div>
                <div className='ApyTermsTable__table' role='table'>
                    <Row sm={3} md={showLoyaltyRates() ? 4 : 3} className='ApyTermsTable__tableHeader' role='row'>
                        <Col role='columnheader'>Term</Col>
                        <Col role='columnheader'>APY</Col>
                        <Col role='columnheader'>{showLoyaltyRates() ? 'Loyalty Rates' : ''}</Col>
                        <Col className={showLoyaltyRates() ? 'md:hidden' : 'hidden'} role='columnheader'></Col>
                    </Row>
                    {terms && terms.map((product, index) => {                    
                        return (
                            <div key={index} className={`${index % 2 !== 0 ? 'bg-grey-ash': ''}`} role='rowgroup' >
                                {product.active && 
                            <Row className='ApyTermsTable__tableContent' role='row'>
                                <Col className='ApyTermsTable__term' xs={2} md={showLoyaltyRates() ? 3 : 4} role='cell'>
                                    {product.term} 
                                    {width > 768 ? ' Months' : ' Mo.'}{showDagger(product.group)}
                                </Col>
                                <Col xs={2} md={showLoyaltyRates() ? 3 : 4} role='cell'>{product.apy}%</Col>
                                {showLoyaltyRates() && <Col xs={2} md={showLoyaltyRates() ? 3 : 4} role='cell'>{product.loyaltyReward}%</Col>}
                                <Col xs={showLoyaltyRates() ? 6 : 2} md={showLoyaltyRates() ? 3 : 4} role='cell'><a className='ApyTermsTable__link' href={cleanInternalLinkUrl(product.url + '.html' )} id={'APY_TermsSubmit_' + index}>{product.buttonText}</a></Col>
                            </Row> }
                            </div>
                        );
                    })
                    }                       
                </div>
            </div>
        </Section>
    );
};

export default ApyTermsTable;
